(() => {
  if (customElements.get('blog-discovery')) return;
  const indexes = new Map();
  const safeUrl = (value, image = false) => {
    try {
      const url = new URL(value, location.origin);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
      return image || url.origin === location.origin ? url.href : '';
    } catch { return ''; }
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };
  async function loadIndex(source) {
    let next = source;
    let title = '';
    let blogUrl = source;
    const articles = new Map();
    const visited = new Set();
    while (next) {
      const url = new URL(next, location.origin);
      if (url.origin !== location.origin || visited.has(url.href)) break;
      visited.add(url.href);
      url.searchParams.set('section_id', 'blog-article-feed');
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Article index unavailable');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const feed = JSON.parse(document.querySelector('[data-journal-feed]').textContent);
      feed.articles.forEach(article => articles.set(article.url, article));
      title = feed.blog_title;
      blogUrl = feed.blog_url;
      next = feed.next;
    }
    return { title, url: blogUrl, articles: [...articles.values()].sort((a, b) => Number(b.published) - Number(a.published)) };
  }
  async function discoverBlogs(root, fallback) {
    const readXml = async path => {
      const response = await fetch(path, { headers: { Accept: 'application/xml' } });
      if (!response.ok) throw new Error('Blog directory unavailable');
      return new DOMParser().parseFromString(await response.text(), 'application/xml');
    };
    const prefix = root.endsWith('/') ? root : root + '/';
    const directory = await readXml('/sitemap.xml');
    const sitemapPaths = [...directory.querySelectorAll('sitemap > loc')].map(node => new URL(node.textContent).pathname).filter(path => path.startsWith(prefix + 'sitemap_blogs_'));
    if (!sitemapPaths.length) throw new Error('No blog sitemap');
    const documents = await Promise.all(sitemapPaths.map(readXml));
    const paths = [...new Set(documents.flatMap(doc => [...doc.querySelectorAll('url > loc')].map(node => new URL(node.textContent).pathname).filter(path => /\/blogs\/[^/]+\/?$/.test(path))))];
    if (!paths.length && fallback) paths.push(fallback);
    const groups = await Promise.all(paths.map(loadIndex));
    return { articles: groups.flatMap(group => group.articles), groups };
  }
  customElements.define('blog-discovery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.input = this.querySelector('input[type=search]');
      this.results = this.querySelector('[data-journal-results]');
      this.status = this.querySelector('[data-journal-status]');
      this.message = this.querySelector('[data-journal-message]');
      this.heading = this.querySelector('[data-journal-heading]');
      this.version = 0;
      this.input.addEventListener('input', () => this.search(), { signal: this.controller.signal });
      this.initialize();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.request?.abort();
      clearTimeout(this.timer);
      this.controller = null;
    }
    async initialize() {
      const source = this.dataset.source;
      if (!source) return;
      try {
        if (!indexes.has(source)) indexes.set(source, discoverBlogs(this.dataset.root || '/', source).catch(error => { indexes.delete(source); throw error; }));
        this.index = await indexes.get(source);
        if (!this.isConnected) return;
        const shuffled = [...this.index.articles];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        this.recommendations = shuffled.slice(0, 3);
        if (!this.input.value.trim()) this.showRecommendations();
        this.categories();
      } catch { this.status.textContent = this.dataset.error; }
    }
    card(article) {
      const link = element('a', 'journal-card');
      link.href = safeUrl(article.url);
      const media = element('div', 'journal-card__image');
      const source = safeUrl(typeof article.image === 'object' ? article.image?.url : article.image, true);
      if (source) {
        const image = element('img');
        image.src = source;
        image.alt = article.alt || article.title || '';
        image.loading = 'lazy';
        image.width = 800;
        image.height = 550;
        media.append(image);
      } else media.append(element('div', 'media-fallback'));
      const arrow = element('span', '', '↗');
      arrow.setAttribute('aria-hidden', 'true');
      media.append(arrow);
      const copy = element('div', 'journal-card__copy');
      if (article.date) copy.append(element('p', 'article-meta', article.date));
      copy.append(element('h3', '', article.title));
      if (article.excerpt) copy.append(element('p', '', article.excerpt));
      copy.append(element('span', 'text-link', this.dataset.read + ' →'));
      link.append(media, copy);
      return link;
    }
    render(articles) { this.results.replaceChildren(...articles.slice(0, 3).map(article => this.card(article))); }
    showRecommendations() {
      this.heading.textContent = this.dataset.recommended;
      this.message.hidden = true;
      this.results.removeAttribute('aria-busy');
      if (this.recommendations) this.render(this.recommendations);
      this.status.textContent = this.dataset.recommended;
    }
    categories() {
      const groups = this.index.groups;
      this.querySelector('[data-journal-categories]').replaceChildren(...groups.map(group => {
        const section = element('section', 'journal__category');
        const header = element('header');
        const intro = element('div');
        intro.append(element('p', 'eyebrow', this.dataset.categories), element('h2', '', group.title));
        const more = element('a', 'text-link', this.dataset.more + ' ↗');
        more.href = safeUrl(group.url);
        header.append(intro, more);
        const grid = element('div', 'journal__grid');
        grid.append(...group.articles.slice(0, 3).map(article => this.card(article)));
        if (!group.articles.length) grid.append(element('p', '', this.dataset.categoryEmpty));
        section.append(header, grid);
        return section;
      }));
    }
    search() {
      clearTimeout(this.timer);
      this.request?.abort();
      const version = ++this.version;
      const query = this.input.value.trim();
      if (!query) { this.showRecommendations(); return; }
      this.heading.textContent = this.dataset.results;
      this.status.textContent = this.dataset.loading;
      this.message.hidden = true;
      this.results.replaceChildren();
      this.results.setAttribute('aria-busy', 'true');
      this.timer = setTimeout(async () => {
        this.request = new AbortController();
        try {
          const url = new URL(this.dataset.suggest, location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('resources[type]', 'article');
          url.searchParams.set('resources[limit]', '3');
          const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Search unavailable');
          const data = await response.json();
          if (version !== this.version || !this.isConnected) return;
          const articles = (data.resources?.results?.articles || []).slice(0, 3);
          this.render(articles);
          this.status.textContent = articles.length ? this.dataset.results + ': ' + articles.length : this.dataset.empty;
          this.message.hidden = articles.length > 0;
          this.message.textContent = this.dataset.empty;
        } catch (error) {
          if (error.name === 'AbortError' || version !== this.version) return;
          this.status.textContent = this.dataset.error;
          this.message.textContent = this.dataset.error;
          this.message.hidden = false;
        } finally {
          if (version === this.version) this.results.removeAttribute('aria-busy');
        }
      }, 180);
    }
  });
})();
